import { ApprovalApprover } from '../../../../Interfaces/api/catalog/approval-approver.interface';
import { resolveApprovalApproverDisplay } from '../../../Catalog/Shared/approval-approver-display.util';
import { gradientFromSeed } from '../../../Catalog/Shared/icon-palette.util';
import { resolveIconUrl } from '../../../Catalog/Shared/icon-url.util';
import {
  ALL_DEMANDS_LIST_ROUTE,
  REPORT_ACTIVE_STATUSES,
} from '../Consts/all-demands-display.const';
import { AllDemandsDisplayRow } from '../Interfaces/all-demands-display-row.interface';
import { ReportDemandRow } from '../Types/report-demand-row.type';
import { formatReportPersonName } from './report-person-name.util';

interface SubjectFacts {
  id: string | undefined;
  name: string;
  iconUrl: string | null | undefined;
  deleted: boolean;
  deletedFallbackKey: string;
  typeKey: string;
}

function subjectFactsOf(row: ReportDemandRow): SubjectFacts {
  if (row.subject_type === 'application') {
    return {
      id: row.application?.id,
      name: row.application?.name ?? '',
      iconUrl: row.application?.icon_url,
      deleted: row.subject_deleted ?? !row.application,
      deletedFallbackKey: 'app.catalog.myDemands.subjectFallbackDeletedApplication',
      typeKey: 'app.reports.allDemands.types.application',
    };
  }
  return {
    id: row.group?.id,
    name: row.group?.name ?? '',
    iconUrl: null,
    deleted: row.subject_deleted ?? !row.group,
    deletedFallbackKey: 'app.catalog.myDemands.subjectFallbackDeletedGroup',
    typeKey: 'app.reports.allDemands.types.group',
  };
}

function subjectFallbackOf(
  subject: SubjectFacts,
  reference: string,
): Pick<AllDemandsDisplayRow, 'subjectFallbackKey' | 'subjectFallbackParams'> {
  if (subject.name.trim().length > 0) return {};
  if (subject.deleted) return { subjectFallbackKey: subject.deletedFallbackKey };
  return {
    subjectFallbackKey: 'app.catalog.myDemands.subjectFallbackRequest',
    subjectFallbackParams: { reference },
  };
}

function approvalDisplayOf(
  current: { type: string; approver: ApprovalApprover } | null | undefined,
): Pick<
  AllDemandsDisplayRow,
  'currentApprovalApproverName' | 'currentApprovalGroupName' | 'currentApprovalTypeKey'
> {
  if (!current) return {};
  const display = resolveApprovalApproverDisplay(current.approver, current.type);
  return {
    currentApprovalApproverName: display.approverName ?? undefined,
    currentApprovalGroupName: display.groupName ?? undefined,
    currentApprovalTypeKey: display.typeKey ?? undefined,
  };
}

export function toAllDemandsDisplayRow(
  row: ReportDemandRow,
  isDark: boolean,
): AllDemandsDisplayRow {
  const subject = subjectFactsOf(row);
  const isApplication = row.subject_type === 'application';
  const iconUrl = resolveIconUrl(subject.iconUrl);

  return {
    id: row.id,
    reference: row.reference,
    subjectName: subject.name,
    ...subjectFallbackOf(subject, row.reference),
    deleted: subject.deleted,
    subjectLink: [ALL_DEMANDS_LIST_ROUTE, row.subject_type, row.id],
    subjectIconUrl: iconUrl,
    hasIcon: isApplication && iconUrl.length > 0,
    isApplication,
    subjectTypeKey: subject.typeKey,
    requesterName: formatReportPersonName(row.requester),
    requesterEmail: row.requester?.email ?? '',
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at ?? row.created_at,
    currentApprovalStep: row.current_approval?.step,
    currentApprovalTotal: row.current_approval?.total_steps,
    ...approvalDisplayOf(row.current_approval),
    isActive: REPORT_ACTIVE_STATUSES.has(row.status),
    closureKey: `app.catalog.demand.status.${row.status}`,
    gradient: gradientFromSeed(subject.id ?? (subject.name || row.id), isDark),
  };
}
